'use client';

import { useEffect, useState, useRef, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import GoogleLogin from './GoogleLogin';

interface User {
    email: string;
    name: string;
    picture: string;
}

interface AuthGuardProps {
    children: React.ReactNode;
}

function AuthGuardContent({ children }: AuthGuardProps) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sessionExpired, setSessionExpired] = useState(false);
    const router = useRouter();
    const searchParams = useSearchParams();
    const sessionCheckInterval = useRef<NodeJS.Timeout | null>(null);
    const hasInitialized = useRef(false);

    useEffect(() => {
        if (!hasInitialized.current) {
            hasInitialized.current = true;
            
            // Check for OAuth errors in URL parameters
            const oauthError = searchParams.get('error');
            if (oauthError) {
                handleOAuthError(oauthError);
                return;
            }
            
            checkAuth();
            
            // Set up periodic session validation (every 60 seconds)
            sessionCheckInterval.current = setInterval(() => {
                validateSession();
            }, 60000);
        }

        return () => {
            if (sessionCheckInterval.current) {
                clearInterval(sessionCheckInterval.current);
            }
        };
    }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOAuthError = (error: string) => {
        let errorMessage = 'Authentication failed';
        
        switch (error) {
            case 'oauth_error':
                errorMessage = 'OAuth authentication failed. Please try again.';
                break;
            case 'missing_state':
                errorMessage = 'Security validation failed. Please try again.';
                break;
            case 'missing_code':
                errorMessage = 'Authorization code missing. Please try again.';
                break;
            case 'missing_credentials':
                errorMessage = 'OAuth configuration error. Please contact the administrator.';
                break;
            case 'token_exchange_failed':
                errorMessage = 'Token exchange failed. Please try again.';
                break;
            case 'no_id_token':
                errorMessage = 'No ID token received. Please try again.';
                break;
            case 'invalid_token':
                errorMessage = 'Invalid authentication token. Please try again.';
                break;
            case 'unauthorized':
                errorMessage = 'Access denied. Your email is not authorized to view analytics.';
                break;
            case 'auth_failed':
                errorMessage = 'Authentication failed. Please try again.';
                break;
            default:
                errorMessage = `Authentication error: ${error}`;
        }
        
        setError(errorMessage);
        setIsLoading(false);
        
        // Clean up URL parameters
        const url = new URL(window.location.href);
        url.searchParams.delete('error');
        window.history.replaceState({}, '', url.toString());
    };

    const checkAuth = async () => {
        try {
            // Use server-side verification for better security
            const response = await fetch('/api/auth/verify');
            
            if (response.ok) {
                const data = await response.json();
                if (data.authenticated && data.authorized) {
                    setUser(data.user);
                    setSessionExpired(false);
                    setError(null);
                } else {
                    // Not authenticated or not authorized
                    setUser(null);
                    if (data.error) {
                        setError(data.error);
                    }
                }
            } else if (response.status === 401) {
                // Not authenticated, show login
                setUser(null);
                setSessionExpired(false);
                setError(null);
            } else if (response.status === 403) {
                // Not authorized, redirect to forbidden page
                router.push('/analytics/forbidden');
                return;
            } else {
                throw new Error('Failed to check authentication');
            }
        } catch (err) {
            console.error('Auth check error:', err);
            setError('Failed to check authentication');
        } finally {
            setIsLoading(false);
        }
    };

    const validateSession = async () => {
        if (!user) return; // Only validate if user is logged in
        
        try {
            const response = await fetch('/api/auth/verify');
            
            if (response.status === 401) {
                // Session expired - force logout
                console.log('🔄 Session expired - forcing logout');
                setSessionExpired(true);
                setUser(null);
                
                // Clear any existing session cookies
                document.cookie = 'analytics_session=; Path=/; HttpOnly; Secure; SameSite=Strict; Max-Age=0';
                
            } else if (response.status === 403) {
                // Not authorized - redirect to forbidden
                router.push('/analytics/forbidden');
            }
        } catch (err) {
            console.error('Session validation error:', err);
        }
    };

    const handleLogout = async () => {
        try {
            await fetch('/api/auth/logout', { method: 'POST' });
            setUser(null);
            setSessionExpired(false);
            setError(null);
            router.push('/analytics');
        } catch (err) {
            console.error('Logout error:', err);
        }
    };

    const handleRetry = () => {
        setError(null);
        setSessionExpired(false);
        setIsLoading(true);
        checkAuth();
    };

    const handleLoginSuccess = (userData: User) => {
        setUser(userData);
        setSessionExpired(false);
        setError(null);
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-black">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                    <p className="text-gray-300">Checking authentication...</p>
                </div>
            </div>
        );
    }

    if (sessionExpired) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-black">
                <div className="text-center max-w-md mx-auto p-6">
                    <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-6">
                        <h2 className="text-2xl font-bold text-yellow-400 mb-4">Session Expired</h2>
                        <p className="text-gray-300 mb-4">
                            Your session has expired due to a new deployment. Please log in again.
                        </p>
                        <button
                            onClick={handleRetry}
                            className="bg-yellow-600 text-white px-6 py-2 rounded-md hover:bg-yellow-700 font-medium transition-colors"
                        >
                            Log In Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-black">
                <div className="text-center max-w-md mx-auto p-6">
                    <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6">
                        <h2 className="text-2xl font-bold text-red-400 mb-4">Authentication Error</h2>
                        <p className="text-gray-300 mb-4">{error}</p>
                        <button
                            onClick={handleRetry}
                            className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 font-medium transition-colors"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!user) {
        return <GoogleLogin onSuccess={handleLoginSuccess} onError={setError} />;
    }

    return (
        <div className="bg-black text-white">
            {/* Header with user info and logout */}
            <div className="bg-gray-900 shadow-sm border-b border-gray-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <div className="flex items-center space-x-4">
                            <h1 className="text-xl font-semibold text-white">
                                Analytics Dashboard
                            </h1>
                            <div className="flex items-center space-x-2 text-sm text-gray-400">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span>Session Active</span>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <Image
                                    src={user.picture}
                                    alt={user.name}
                                    width={32}
                                    height={32}
                                    className="w-8 h-8 rounded-full"
                                />
                                <span className="text-sm text-gray-300">{user.name}</span>
                            </div>
                            <button
                                onClick={handleLogout}
                                className="text-sm text-gray-400 hover:text-white transition-colors"
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {children}
            </div>
        </div>
    );
}

export default function AuthGuard({ children }: AuthGuardProps) {
    return (
        <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-black">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                    <p className="text-gray-300">Loading...</p>
                </div>
            </div>
        }>
            <AuthGuardContent>{children}</AuthGuardContent>
        </Suspense>
    );
}